import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Building2, Plus } from 'lucide-react'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { cn } from '@/lib/utils'

export function WorkspaceSwitcher() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { workspaces, activeWorkspace, setActiveWorkspace } = useWorkspace()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (workspaces.length === 0) return null

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium',
          'text-gray-700 hover:bg-gray-100 transition-colors',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 className="h-4 w-4 text-gray-500 shrink-0" />
        <span className="max-w-[140px] truncate">
          {activeWorkspace?.name ?? t('workspaces.switchWorkspace')}
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('workspaces.switchWorkspace')}
          className={cn(
            'absolute right-0 top-full mt-1 z-50 min-w-[180px] max-w-[240px]',
            'rounded-lg border border-gray-200 bg-white shadow-lg py-1',
          )}
        >
          {workspaces.map((ws) => (
            <button
              key={ws.id}
              role="option"
              aria-selected={ws.id === activeWorkspace?.id}
              type="button"
              onClick={() => {
                setActiveWorkspace(ws)
                setOpen(false)
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-sm text-left',
                'hover:bg-gray-50 transition-colors',
                ws.id === activeWorkspace?.id && 'font-semibold text-indigo-700',
              )}
            >
              <Building2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{ws.name}</span>
              <span className="ml-auto text-xs text-gray-400 shrink-0">
                {t(`workspaces.${ws.role}`)}
              </span>
            </button>
          ))}

          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                navigate('/workspaces')
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('workspaces.new')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
